import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import categoryService from '../../api/services/categoryService';
import contestantService from '../../api/services/contestantService';
import { handleApiError } from '../../utils/helpers';
import FileInput from '../../components/common/Fileinput';
import { getImageUrl } from '../../utils/imageHelper';

const CategoryContestantsPage = () => {
  const { contestId, categoryId } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();

  const [category, setCategory] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [contestantModalOpen, setContestantModalOpen] = useState(false);
  const [editingContestant, setEditingContestant] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contestantToDelete, setContestantToDelete] = useState(null);

  useEffect(() => {
    fetchCategoryData();
  }, [categoryId]);

  const fetchCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch category details
      const categoryResponse = await categoryService.getCategoryById(categoryId);
      
      if (categoryResponse.success) {
        setCategory(categoryResponse.data);
      }

      // Fetch contestants for this category
      const contestantsResponse = await contestantService.getCategoryContestants(categoryId);
      
      if (contestantsResponse.success) {
        setContestants(contestantsResponse.data || []);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddContestant = () => {
    setEditingContestant(null);
    setContestantModalOpen(true);
  };

  const handleEditContestant = (contestant) => {
    setEditingContestant(contestant);
    setContestantModalOpen(true);
  };

  const handleDeleteClick = (contestant) => {
    setContestantToDelete(contestant);
    setDeleteDialogOpen(true);
  };

  const handleDeleteContestant = async () => {
    try {
      const response = await contestantService.deleteContestant(contestantToDelete.id);
      
      if (response.success) {
        success('Contestant deleted successfully!');
        fetchCategoryData();
        setDeleteDialogOpen(false);
        setContestantToDelete(null);
      }
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  // Get image URL helper
  // const getImageUrl = (path) => {
  //   if (!path) return null;
  //   if (path.startsWith('http')) return path;
  //   const normalizedPath = path.replace(/\\/g, '/');
  //   const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
  //   return `${BASE_URL}/${normalizedPath}`;
  // };

  if (loading) {
    return (
      <DashboardLayout title="Category Contestants">
        <div className="flex items-center justify-center min-h-96">
          <Loader size="lg" text="Loading contestants..." />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Category Contestants">
        <ErrorMessage message={error} onRetry={fetchCategoryData} />
      </DashboardLayout>
    );
  }

  if (!category) {
    return (
      <DashboardLayout title="Category Contestants">
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600">Category not found</p>
            <Button variant="outline" onClick={() => navigate(`/admin/contests/${contestId}`)} className="mt-4">
              Back to Contest
            </Button>
          </div>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={category.name}
      subtitle={`Manage contestants for ${category.name}`}
    >
      {/* Back Button */}
      <div className="mb-6">
        <Button variant="outline" size="sm" onClick={() => navigate(`/admin/contests/${contestId}`)}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Contest
        </Button>
      </div>

      {/* Category Info */}
      <Card className="mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{category.name}</h2>
            <p className="text-gray-600 mb-4">{category.description}</p>
            
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>{contestants.length} Contestants</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>{category._count?.votes || 0} Total Votes</span>
              </div>
              {category.contest && (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{category.contest.title}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Contestants Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Contestants</h3>
          <Button variant="primary" onClick={handleAddContestant}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Contestant
          </Button>
        </div>

        {contestants.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No contestants yet</h3>
              <p className="mt-1 text-gray-500">Get started by adding your first contestant</p>
              <div className="mt-6">
                <Button variant="primary" onClick={handleAddContestant}>
                  Add Contestant
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {contestants.map((contestant) => (
              <ContestantCard
                key={contestant.id}
                contestant={contestant}
                onEdit={() => handleEditContestant(contestant)}
                onDelete={() => handleDeleteClick(contestant)}
                getImageUrl={getImageUrl}
              />
            ))}
          </div>
        )}
      </div>

      {/* Contestant Modal */}
      <ContestantFormModal
        isOpen={contestantModalOpen}
        onClose={() => {
          setContestantModalOpen(false);
          setEditingContestant(null);
        }}
        onSuccess={() => {
          fetchCategoryData();
          setContestantModalOpen(false);
          setEditingContestant(null);
        }}
        categoryId={categoryId}
        contestant={editingContestant}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteContestant}
        title="Delete Contestant"
        message={`Are you sure you want to delete "${contestantToDelete?.name}"? This will also delete all votes for this contestant.`}
        confirmText="Delete"
        variant="danger"
      />
    </DashboardLayout>
  );
};

// Contestant Card Component
const ContestantCard = ({ contestant, onEdit, onDelete, getImageUrl }) => {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      {/* Photo */}
      {contestant.photo ? (
        <div className="w-full h-48 mb-3 rounded-lg overflow-hidden">
          <img
            src={getImageUrl(contestant.photo)}
            alt={contestant.name}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-full h-48 mb-3 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
          <svg className="w-20 h-20 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      )}

      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="text-lg font-semibold text-gray-900">{contestant.name}</h4>
          {/* ✅ Display Contestant Code */}
          
        </div>
        {contestant.contestantCode && (
            <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
              {contestant.contestantCode}
            </span>
          )}
        {/* {contestant.order !== undefined && (
          <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
            #{contestant.order}
          </span>
        )} */}
      </div>

      <p className="text-sm text-gray-600 line-clamp-3 mb-4">{contestant.bio}</p>

      {/* <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
        <div className="flex items-center">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span className="font-medium">{contestant._count?.votes || 0} votes</span>
        </div>
      </div> */}

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onEdit} className="flex-1">
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={onDelete} className="flex-1">
          Delete
        </Button>
      </div>
    </Card>
  );
};

// Contestant Form Modal Component
const ContestantFormModal = ({ isOpen, onClose, onSuccess, categoryId, contestant }) => {
  const { success, error: showError } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    order: '0',
    contestantCode: '', // ✅ ADD CONTESTANT CODE
    photo: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (contestant) {
      setFormData({
        name: contestant.name || '',
        bio: contestant.bio || '',
        order: contestant.order?.toString() || '0',
        contestantCode: contestant.contestantCode || '', // ✅ POPULATE CODE
        photo: null // Don't pre-fill the photo
      });
    } else {
      setFormData({
        name: '',
        bio: '',
        order: '0',
        contestantCode: '', // ✅ RESET CODE
        photo: null
      });
    }
    // Clear errors when modal opens/closes
    setErrors({});
  }, [contestant, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (file) => {
    setFormData(prev => ({ ...prev, photo: file }));
    if (errors.photo) {
      setErrors(prev => ({ ...prev, photo: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Contestant name is required';
    }
    
    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required';
    }
    
    // ✅ Validate contestant code format if provided
    // if (formData.contestantCode.trim()) {
    //   const cleanCode = formData.contestantCode.trim().toUpperCase();
    //   if (!/^[A-Z0-9]{3,10}$/.test(cleanCode)) {
    //     newErrors.contestantCode = 'Code must be 3-10 alphanumeric characters (e.g., BA001, MG023)';
    //   }
    // }
    
    // Photo is required only when creating a new contestant
    if (!contestant && !formData.photo) {
      newErrors.photo = 'Photo is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const data = new FormData();
      data.append('categoryId', categoryId);
      data.append('name', formData.name.trim());
      data.append('bio', formData.bio.trim());
      data.append('order', formData.order);
      
      // ✅ ADD CONTESTANT CODE TO FORM DATA
      if (formData.contestantCode.trim()) {
        data.append('contestantCode', formData.contestantCode.trim().toUpperCase());
      } else {
        data.append('contestantCode', ''); // Send empty string to remove code
      }
      
      if (formData.photo) {
        data.append('photo', formData.photo);
      }

      const response = contestant
        ? await contestantService.updateContestant(contestant.id, data)
        : await contestantService.createContestant(data);

      if (response.success) {
        success(`Contestant ${contestant ? 'updated' : 'created'} successfully!`);
        onSuccess();
      }
    } catch (err) {
      showError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={contestant ? 'Edit Contestant' : 'Add Contestant'}
      size="lg"
    >
      <form onSubmit={handleSubmit}>
        <Input
          label="Contestant Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="King Promise"
          error={errors.name}
          required
        />

        {/* ✅ ADD CONTESTANT CODE INPUT */}
        <Input
  label="Contestant Code (Optional)"
  name="contestantCode"
  value={formData.contestantCode}
  onChange={handleChange}
  placeholder="Enter any code or text"
  error={errors.contestantCode}
/>
        {/* <Input
          label="Contestant Code (Optional)"
          name="contestantCode"
          value={formData.contestantCode}
          onChange={handleChange}
          placeholder="BA001, MG023, etc."
          error={errors.contestantCode}
          helperText="3-10 alphanumeric characters (letters and numbers only)"
          maxLength={10}
        /> */}

        <Input
          label="Bio"
          name="bio"
          value={formData.bio}
          onChange={handleChange}
          placeholder="Award-winning rapper from Ghana"
          error={errors.bio}
          rows={4}
          required
        />

        <Input
          label="Display Order"
          type="number"
          name="order"
          value={formData.order}
          onChange={handleChange}
          placeholder="0"
          min="0"
          error={errors.order}
        />

        <FileInput
          label="Photo"
          name="photo"
          onChange={handleFileChange}
          accept="image/*"
          preview
          error={errors.photo}
          required={!contestant}
          helperText={contestant ? "Leave empty to keep current photo" : ""}
        />

        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="flex-1"
          >
            {contestant ? 'Update' : 'Add'} Contestant
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoryContestantsPage;